/**
 * FlexLoadGantt - Gantt-style chart showing flexible load scheduling over 24 hours.
 *
 * Props:
 *   - flexSchedules: Array of flexible asset schedules
 *     Format: [{ building_id, asset_name, asset_type, hours_on: boolean[24] }]
 *
 * Displays:
 *   - Y-axis: List of flexible assets (Heat Pump B1, EV Charger B2, etc.)
 *   - X-axis: Hours 0-23
 *   - Horizontal bars showing when each asset is ON (scheduled to run)
 *   - Color-coded by asset type
 *
 * Uses ApexCharts rangeBar chart type for Gantt visualization.
 */

import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'

// Generate mock flex load schedule data
const generateMockFlexSchedules = () => {
  return [
    {
      building_id: 'Building-A',
      asset_name: 'Heat Pump',
      asset_type: 'heat_pump',
      hours_on: [
        true, true, true, true, true, true, // 0-5: night heating
        false, false, false, false, false, false, false, false, // 6-13: off
        true, true, true, // 14-16: pre-heating
        false, false, false, false, false, false, false, // 17-23: off
      ],
    },
    {
      building_id: 'Building-B',
      asset_name: 'HVAC',
      asset_type: 'hvac',
      hours_on: [
        false, false, false, false, false, false, false, false, // 0-7: off
        true, true, true, true, // 8-11: morning cooling
        false, false, // 12-13: off
        true, true, true, true, // 14-17: afternoon cooling
        false, false, false, false, false, false, // 18-23: off
      ],
    },
    {
      building_id: 'Building-A',
      asset_name: 'EV Charger',
      asset_type: 'ev_charger',
      hours_on: [
        true, true, true, true, true, true, // 0-5: overnight charging
        false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, // 6-22: off
        true, // 23: start charging
      ],
    },
    {
      building_id: 'Building-C',
      asset_name: 'Washing Machine',
      asset_type: 'flexible_appliance',
      hours_on: [
        false, false, false, false, false, false, false, false, false, false, false, false, false, // 0-12: off
        true, true, // 13-14: shifted to solar peak
        false, false, false, false, false, false, false, false, false, // 15-23: off
      ],
    },
  ]
}

function FlexLoadGantt({ flexSchedules }) {
  // Use real optimizer data when available. Show empty state when no flex assets exist.
  const hasRealData = Array.isArray(flexSchedules)
  const schedules = hasRealData && flexSchedules.length > 0 ? flexSchedules : []

  // Only include schedules that have at least one active hour — used for tooltip indexing
  const activeSchedules = schedules.filter(
    (s) => Array.isArray(s.hours_on) && s.hours_on.some(Boolean)
  )

  // Color mapping by asset type
  const colorMap = {
    heat_pump: '#3498DB', // Blue
    hvac: '#1ABC9C', // Cyan
    ev_charger: '#F39C12', // Orange
    flexible_appliance: '#9B59B6', // Purple
  }

  // Transform hours_on boolean array to rangeBar data format
  const chartData = useMemo(() => {
    const series = activeSchedules.map((schedule) => {
        // Find contiguous ON periods and create range bars
        const ranges = []
        let startHour = null

        schedule.hours_on.forEach((isOn, hour) => {
          if (isOn && startHour === null) {
            // Start of a new ON period
            startHour = hour
          } else if (!isOn && startHour !== null) {
            // End of an ON period
            ranges.push({
              x: `${schedule.building_id} / ${schedule.asset_name}`,
              y: [startHour, hour],
            })
            startHour = null
          }
        })

        // Handle case where ON period extends to end of day
        if (startHour !== null) {
          ranges.push({
            x: `${schedule.building_id} / ${schedule.asset_name}`,
            y: [startHour, 24],
          })
        }

        return {
          name: `${schedule.building_id} / ${schedule.asset_name}`,
          data: ranges,
          color: colorMap[schedule.asset_type] || '#95A5A6',
        }
      })

    return series
  }, [schedules])

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: 'rangeBar',
      height: 300,
      toolbar: { show: true },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: false,
        barHeight: '80%',
        rangeBarGroupRows: false,
      },
    },
    // Colors must match the filtered series (only active schedules with at least one hour ON)
    colors: activeSchedules.map((s) => colorMap[s.asset_type] || '#95A5A6'),
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: 'numeric',
      min: 0,
      max: 24,
      tickAmount: 24,
      title: {
        text: 'Hour of Day',
        style: { fontSize: '12px', fontWeight: 500 },
      },
      labels: {
        style: { fontSize: '11px' },
        formatter: (value) => value != null ? Math.floor(value).toString() : '',
      },
    },
    yaxis: {
      show: true,
      labels: {
        style: { fontSize: '11px' },
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        // seriesIndex maps to activeSchedules (filtered), not the original schedules array
        const schedule = activeSchedules[seriesIndex]
        if (!schedule) return ''
        const range = w.config.series[seriesIndex].data[dataPointIndex]
        if (!range) return ''
        return `
          <div style="padding: 8px; background: white; border: 1px solid #e0e0e0; border-radius: 4px; font-size: 12px;">
            <div style="font-weight: 600;">${schedule.building_id} / ${schedule.asset_name}</div>
            <div style="color: #666; margin-top: 4px;">ON: ${range.y[0]}:00 - ${range.y[1]}:00</div>
            <div style="color: #666;">Type: ${schedule.asset_type.replace(/_/g, ' ')}</div>
          </div>
        `
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 2 },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
  }

  // Empty state when no flex assets exist in this community
  if (schedules.length === 0) {
    return (
      <div className="gantt-chart-container">
        <h6
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            marginBottom: 'var(--space-3)',
            color: 'var(--color-neutral-700)',
          }}
        >
          Flexible Load Scheduling (24-hour Gantt)
        </h6>
        <div className="text-center py-4" style={{ color: 'var(--color-neutral-500)' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, margin: 0 }}>
            No flexible loads configured
          </p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-400)', marginTop: 'var(--space-2)' }}>
            Add heat pumps, EV chargers, or HVAC systems to buildings to enable flexible load scheduling.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="gantt-chart-container">
      <h6
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 600,
          marginBottom: 'var(--space-3)',
          color: 'var(--color-neutral-700)',
        }}
      >
        Flexible Load Scheduling (24-hour Gantt)
      </h6>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-3)' }}>
        Optimized ON/OFF schedule for each flexible asset. Bars show when each device is scheduled to run.
      </p>
      <Chart options={chartOptions} series={chartData} type="rangeBar" height={300} />
    </div>
  )
}

export default FlexLoadGantt
